import React, { useState, useEffect } from 'react';
import { FinancialRecordFilter, FinancialRecord } from '../../types/data/financialRecord';
import financialRecordKPIService from '../../services/financialRecordKPIService';
import { User } from '../../types/user';
import { useTheme } from '../../contexts/ThemeContext';
import './FinancialRecordFilterPanel.css';

interface FinancialRecordFilterPanelProps {
  initialFilter?: FinancialRecordFilter | null;
  onApplyFilter: (filter: FinancialRecordFilter, filterMode?: 'dynamic' | 'static') => void;
  onClearFilter: () => void;
  availableUsers?: Array<{ id: string; name: string }>; // Available users for owner filtering
  showOwnerFilter?: boolean; // Whether to show the owner filter (for admins/group admins)
  records?: FinancialRecord[]; // Kept for backward compatibility - not used for filter options
  userMap?: Map<string, User>; // Map of user IDs to user objects
  filterUserIds?: string[]; // List of user IDs for filtering
  // Direct filter options (more efficient than extracting from records)
  availableOptions?: {
    brands: string[];
    dealers: string[];
    years: number[];
    months: number[];
    kpiCategories: string[];
  };
}

// Local state interface for easier form management
interface LocalFilterState {
  dealers: string[];
  brands: string[];
  years: number[];
  months: number[];
  kpiCategories: string[];
  owners: string[];
}

// Interface for available filter options
interface AvailableOptions {
  brands: string[];
  dealers: string[];
  years: number[];
  months: number[];
  kpiCategories: string[];
}

// Filter mode type
type FilterMode = 'dynamic' | 'static';

const FinancialRecordFilterPanel: React.FC<FinancialRecordFilterPanelProps> = ({
  initialFilter,
  onApplyFilter,
  onClearFilter,
  availableUsers = [],
  showOwnerFilter = false,
  records = [],
  userMap,
  filterUserIds,
  availableOptions: propAvailableOptions
}) => {
  // Get current theme
  const { theme } = useTheme();
  const [filter, setFilter] = useState<LocalFilterState>({
    dealers: initialFilter?.dealers || [],
    brands: initialFilter?.brands || [],
    years: initialFilter?.years || [],
    months: initialFilter?.months || [],
    kpiCategories: initialFilter?.kpiCategories || [],
    owners: initialFilter?.owners || []
  });

  // Form input states for easier UI handling
  const [dealerInput, setDealerInput] = useState('');
  const [brandInput, setBrandInput] = useState('');
  const [yearInput, setYearInput] = useState('');
  const [monthInput, setMonthInput] = useState('');
  const [categoryInput, setCategoryInput] = useState('');
  const [ownerInput, setOwnerInput] = useState('');

  // Filter mode state - default to dynamic (current behavior)
  const [filterMode, setFilterMode] = useState<FilterMode>('dynamic');

  // Available options from financial records
  const [availableOptions, setAvailableOptions] = useState<AvailableOptions>({
    brands: [],
    dealers: [],
    years: [],
    months: [],
    kpiCategories: []
  });
  
  // Store the original/static options (all available options regardless of filters)
  const [staticOptions, setStaticOptions] = useState<AvailableOptions>({
    brands: [],
    dealers: [],
    years: [],
    months: [],
    kpiCategories: []
  });
  
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isAutoApplying, setIsAutoApplying] = useState(false);
  
  // Track if we've received direct filter options to avoid falling back unnecessarily
  const [hasReceivedDirectOptions, setHasReceivedDirectOptions] = useState(false);
  
  // Use direct filter options if provided, otherwise extract from records (fallback)
  useEffect(() => {
                        if (propAvailableOptions) {
      const hasData = (
        (propAvailableOptions.brands && propAvailableOptions.brands.length > 0) ||
        (propAvailableOptions.dealers && propAvailableOptions.dealers.length > 0) ||
        (propAvailableOptions.years && propAvailableOptions.years.length > 0) ||
        (propAvailableOptions.months && propAvailableOptions.months.length > 0) ||
        (propAvailableOptions.kpiCategories && propAvailableOptions.kpiCategories.length > 0)
      );
      
                  const newOptions = {
        brands: propAvailableOptions.brands || [],
        dealers: propAvailableOptions.dealers || [],
        years: propAvailableOptions.years || [],
        months: propAvailableOptions.months || [],
        kpiCategories: propAvailableOptions.kpiCategories || []
      };
      
      // In static mode, ignore dynamic updates from parent and keep static options
      if (filterMode === 'static' && hasReceivedDirectOptions) {
                // Don't update availableOptions in static mode, keep using staticOptions
        return;
      }
      
      // In dynamic mode or first time, use the new options
      setAvailableOptions(newOptions);
      
      // Store static options (full set) - if this is the first time receiving options, store as static
      if (!hasReceivedDirectOptions) {
        setStaticOptions(newOptions);
              }
      
      setHasReceivedDirectOptions(true);
      
      if (hasData) {
              } else {
              }
    } else if (!hasReceivedDirectOptions && records && records.length > 0) {
            console.warn('PERFORMANCE WARNING: Using paginated records for filter options instead of dedicated endpoint');
      
      // Fallback: extract from records (less efficient and incomplete)
      const allKpis = records.flatMap(r => r.kpis || []);
      const uniqueBrands = Array.from(new Set(records.map(r => r.brand).filter((b): b is string => Boolean(b)))).sort();
      const uniqueDealers = Array.from(new Set(records.map(r => r.dealer).filter((d): d is string => Boolean(d)))).sort();
      const uniqueYears = Array.from(new Set(records.map(r => r.year))).sort((a, b) => b - a);
      const uniqueMonths = Array.from(new Set(records.map(r => r.month))).sort((a, b) => a - b);
      const uniqueKpiCategories = Array.from(new Set(allKpis.map(kpi => kpi.category).filter((c): c is string => Boolean(c)))).sort();
      
      const newOptions = {
        brands: uniqueBrands,
        dealers: uniqueDealers,
        years: uniqueYears,
        months: uniqueMonths,
        kpiCategories: uniqueKpiCategories
      };
      
      setAvailableOptions(newOptions);
      
      // Store as static options if first time
      if (!hasReceivedDirectOptions) {
        setStaticOptions(newOptions);
      }
    } else if (!hasReceivedDirectOptions) {
            const emptyOptions = {
        brands: [],
        dealers: [],
        years: [],
        months: [],
        kpiCategories: []
      };
      setAvailableOptions(emptyOptions);
      setStaticOptions(emptyOptions);
    } else {
          }
  }, [propAvailableOptions, records, hasReceivedDirectOptions, filterMode]); // Added filterMode to dependencies

  // Effect to handle filter mode changes
  useEffect(() => {
            if (filterMode === 'static') {
      // Switch to static mode - use all available options
            setAvailableOptions(staticOptions);
    } else {
      // Switch to dynamic mode - options will be updated by parent automatically
            // Don't trigger any API calls here - let the parent handle it naturally
    }
  }, [filterMode, staticOptions]);

  // Helper function to auto-apply filters after user actions
  const autoApplyFilter = async (newFilter: LocalFilterState) => {
    try {
      setIsAutoApplying(true);
      
      // Create a filter object with only arrays that have values
      const appliedFilter: FinancialRecordFilter = {};
      
      if (newFilter.dealers.length > 0) appliedFilter.dealers = newFilter.dealers;
      if (newFilter.brands.length > 0) appliedFilter.brands = newFilter.brands;
      if (newFilter.years.length > 0) appliedFilter.years = newFilter.years;
      if (newFilter.months.length > 0) appliedFilter.months = newFilter.months;
      if (newFilter.kpiCategories.length > 0) appliedFilter.kpiCategories = newFilter.kpiCategories;
      if (newFilter.owners.length > 0) appliedFilter.owners = newFilter.owners;
      
                              // Apply the filter with the current filter mode
      onApplyFilter(appliedFilter, filterMode);
      
      // In static mode, don't refresh available options - they should remain the same
      // In dynamic mode, options are automatically updated from records prop
    } finally {
      setIsAutoApplying(false);
    }
  };

  // Update local filter state when initialFilter changes
  useEffect(() => {
    if (initialFilter) {
      setFilter({
        dealers: initialFilter.dealers || [],
        brands: initialFilter.brands || [],
        years: initialFilter.years || [],
        months: initialFilter.months || [],
        kpiCategories: initialFilter.kpiCategories || [],
        owners: initialFilter.owners || []
      });
    } else {
      // Reset the filter form when initialFilter is null
      setFilter({
        dealers: [],
        brands: [],
        years: [],
        months: [],
        kpiCategories: [],
        owners: []
      });
      // Reset input fields
      setDealerInput('');
      setBrandInput('');
      setYearInput('');
      setMonthInput('');
      setCategoryInput('');
    }
  }, [initialFilter]);

  const handleAddDealer = () => {
    if (dealerInput.trim() && !filter.dealers.includes(dealerInput.trim())) {
      const newFilter = { ...filter, dealers: [...filter.dealers, dealerInput.trim()] };
      setFilter(newFilter);
      setDealerInput('');
      // Auto-apply after user action
      setTimeout(() => autoApplyFilter(newFilter), 300);
    }
  };

  const handleDealerSelect = (selectedDealer: string) => {
    setDealerInput(selectedDealer);
    if (selectedDealer && !filter.dealers.includes(selectedDealer)) {
      const newFilter = { ...filter, dealers: [...filter.dealers, selectedDealer] };
      setFilter(newFilter);
      setDealerInput('');
      // Auto-apply after user action
      setTimeout(() => autoApplyFilter(newFilter), 300);
    }
  };

  const handleRemoveDealer = (dealer: string) => {
    const newFilter = { ...filter, dealers: filter.dealers.filter(d => d !== dealer) };
    setFilter(newFilter);
    // Auto-apply after user action
    setTimeout(() => autoApplyFilter(newFilter), 300);
  };

  const handleAddBrand = () => {
    if (brandInput.trim() && !filter.brands.includes(brandInput.trim())) {
      const newFilter = { ...filter, brands: [...filter.brands, brandInput.trim()] };
      setFilter(newFilter);
      setBrandInput('');
      // Auto-apply after user action
      setTimeout(() => autoApplyFilter(newFilter), 300);
    }
  };

  const handleBrandSelect = (selectedBrand: string) => {
    setBrandInput(selectedBrand);
    if (selectedBrand && !filter.brands.includes(selectedBrand)) {
      const newFilter = { ...filter, brands: [...filter.brands, selectedBrand] };
      setFilter(newFilter);
      setBrandInput('');
      // Auto-apply after user action
      setTimeout(() => autoApplyFilter(newFilter), 300);
    }
  };

  const handleRemoveBrand = (brand: string) => {
    const newFilter = { ...filter, brands: filter.brands.filter(b => b !== brand) };
    setFilter(newFilter);
    // Auto-apply after user action
    setTimeout(() => autoApplyFilter(newFilter), 300);
  };

  const handleAddYear = () => {
    const year = parseInt(yearInput);
    if (!isNaN(year) && !filter.years.includes(year)) {
      const newFilter = { ...filter, years: [...filter.years, year] };
      setFilter(newFilter);
      setYearInput('');
      // Auto-apply after user action
      setTimeout(() => autoApplyFilter(newFilter), 300);
    }
  };

  const handleYearSelect = (selectedYear: string) => {
        setYearInput(selectedYear);
    const year = parseInt(selectedYear);
        if (!isNaN(year) && !filter.years.includes(year)) {
      const newFilter = { ...filter, years: [...filter.years, year] };
            setFilter(newFilter);
      setYearInput('');
      // Auto-apply after user action
      setTimeout(() => autoApplyFilter(newFilter), 300);
    } else {
          }
  };

  const handleRemoveYear = (year: number) => {
    const newFilter = { ...filter, years: filter.years.filter(y => y !== year) };
    setFilter(newFilter);
    // Auto-apply after user action
    setTimeout(() => autoApplyFilter(newFilter), 300);
  };

  const handleAddMonth = () => {
    const month = parseInt(monthInput);
    if (!isNaN(month) && month >= 1 && month <= 12 && !filter.months.includes(month)) {
      const newFilter = { ...filter, months: [...filter.months, month] };
      setFilter(newFilter);
      setMonthInput('');
      // Auto-apply after user action
      setTimeout(() => autoApplyFilter(newFilter), 300);
    }
  };

  const handleMonthSelect = (selectedMonth: string) => {
    setMonthInput(selectedMonth);
    const month = parseInt(selectedMonth);
    if (!isNaN(month) && month >= 1 && month <= 12 && !filter.months.includes(month)) {
      const newFilter = { ...filter, months: [...filter.months, month] };
      setFilter(newFilter);
      setMonthInput('');
      // Auto-apply after user action
      setTimeout(() => autoApplyFilter(newFilter), 300);
    }
  };

  const handleRemoveMonth = (month: number) => {
    const newFilter = { ...filter, months: filter.months.filter(m => m !== month) };
    setFilter(newFilter);
    // Auto-apply after user action
    setTimeout(() => autoApplyFilter(newFilter), 300);
  };

  const handleAddCategory = () => {
    if (categoryInput.trim() && !filter.kpiCategories.includes(categoryInput.trim())) {
      const newFilter = { ...filter, kpiCategories: [...filter.kpiCategories, categoryInput.trim()] };
      setFilter(newFilter);
      setCategoryInput('');
      // Auto-apply after user action
      setTimeout(() => autoApplyFilter(newFilter), 300);
    }
  };

  const handleCategorySelect = (selectedCategory: string) => {
    setCategoryInput(selectedCategory);
    if (selectedCategory && !filter.kpiCategories.includes(selectedCategory)) {
      const newFilter = { ...filter, kpiCategories: [...filter.kpiCategories, selectedCategory] };
      setFilter(newFilter);
      setCategoryInput('');
      // Auto-apply after user action
      setTimeout(() => autoApplyFilter(newFilter), 300);
    }
  };

  const handleRemoveCategory = (category: string) => {
    const newFilter = { ...filter, kpiCategories: filter.kpiCategories.filter(c => c !== category) };
    setFilter(newFilter);
    // Auto-apply after user action
    setTimeout(() => autoApplyFilter(newFilter), 300);
  };

  // Owner handlers
  const handleAddOwner = () => {
    if (ownerInput.trim() && !filter.owners.includes(ownerInput.trim())) {
      const newFilter = { ...filter, owners: [...filter.owners, ownerInput.trim()] };
      setFilter(newFilter);
      setOwnerInput('');
      // Auto-apply after user action
      setTimeout(() => autoApplyFilter(newFilter), 300);
    }
  };

  const handleOwnerSelect = (selectedOwnerId: string) => {
                    setOwnerInput(selectedOwnerId);
    if (selectedOwnerId && !filter.owners.includes(selectedOwnerId)) {
      const newFilter = { ...filter, owners: [...filter.owners, selectedOwnerId] };
            setFilter(newFilter);
      setOwnerInput('');
      // Auto-apply after user action
      setTimeout(() => autoApplyFilter(newFilter), 300);
    }
  };

  const handleRemoveOwner = (ownerId: string) => {
    const newFilter = { ...filter, owners: filter.owners.filter(o => o !== ownerId) };
    setFilter(newFilter);
    // Auto-apply after user action
    setTimeout(() => autoApplyFilter(newFilter), 300);
  };

  const handleApplyFilter = async () => {
    // Create a filter object with only arrays that have values
    const appliedFilter: FinancialRecordFilter = {};
    
    if (filter.dealers.length > 0) appliedFilter.dealers = filter.dealers;
    if (filter.brands.length > 0) appliedFilter.brands = filter.brands;
    if (filter.years.length > 0) appliedFilter.years = filter.years;
    if (filter.months.length > 0) appliedFilter.months = filter.months;
    if (filter.kpiCategories.length > 0) appliedFilter.kpiCategories = filter.kpiCategories;
    if (filter.owners.length > 0) appliedFilter.owners = filter.owners;
    
                    // Apply the filter with the current filter mode
    onApplyFilter(appliedFilter, filterMode);
  };

  const handleClearFilter = () => {
    const newFilter = {
      dealers: [],
      brands: [],
      years: [],
      months: [],
      kpiCategories: [],
      owners: []
    };
    setFilter(newFilter);
    setDealerInput('');
    setBrandInput('');
    setYearInput('');
    setMonthInput('');
    setCategoryInput('');
    setOwnerInput('');
    onClearFilter();
    
    // Auto-apply after user action (clearing)
    setTimeout(() => autoApplyFilter(newFilter), 300);
  };

  // Generate months options
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  // Handle refresh options button (no longer fetches from API, just reprocesses existing records)
  const handleRefreshOptions = (e: React.MouseEvent) => {
    e.preventDefault();
    // This is now handled automatically by the useEffect when records change
    console.log('Filter options are automatically updated from records');
  };

  // Count active filters
  const activeFilterCount = filter.dealers.length + filter.brands.length + filter.years.length + filter.months.length + filter.kpiCategories.length + filter.owners.length;

  return (
    <div className="modern-filter-panel">
      {/* Static Filter Header */}
      <div className="filter-header-static">
        <div className="filter-header-left">
          <div className="filter-icon">🔍</div>
          <h3>Filter Records</h3>
          {activeFilterCount > 0 && (
            <span className="filter-badge">{activeFilterCount}</span>
          )}
          {isAutoApplying && (
            <span className="auto-applying-indicator" title="Auto-applying filters...">
              ⏳
            </span>
          )}
        </div>
        <div className="filter-header-right">
          <div className="filter-mode-toggle">
            <span className="filter-mode-label">Filter Mode:</span>
            <div className="toggle-buttons">
              <button
                type="button"
                className={`toggle-btn ${filterMode === 'dynamic' ? 'active' : ''}`}
                onClick={() => {
                  setFilterMode('dynamic');
                  // When switching to dynamic mode, trigger a refresh to get current filtered options
                  const currentFilter: FinancialRecordFilter = {};
                  if (filter.dealers.length > 0) currentFilter.dealers = filter.dealers;
                  if (filter.brands.length > 0) currentFilter.brands = filter.brands;
                  if (filter.years.length > 0) currentFilter.years = filter.years;
                  if (filter.months.length > 0) currentFilter.months = filter.months;
                  if (filter.kpiCategories.length > 0) currentFilter.kpiCategories = filter.kpiCategories;
                  if (filter.owners.length > 0) currentFilter.owners = filter.owners;
                  
                                    onApplyFilter(currentFilter, 'dynamic');
                }}
                title="Dynamic: Filter options update based on current selections"
              >
                🔄 Dynamic
              </button>
              <button
                type="button"
                className={`toggle-btn ${filterMode === 'static' ? 'active' : ''}`}
                onClick={() => {
                  setFilterMode('static');
                  // If we don't have static options yet, request them from parent
                  if (staticOptions.brands.length === 0 && staticOptions.dealers.length === 0 && 
                      staticOptions.years.length === 0 && staticOptions.months.length === 0 && 
                      staticOptions.kpiCategories.length === 0) {
                                        // Trigger the parent to fetch static options (only when switching to static mode)
                    onApplyFilter({}, 'static');
                  }
                }}
                title="Static: All options remain available for multi-selection"
              >
                📌 Static
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Mode Description */}
      <div className="filter-mode-description">
        {filterMode === 'dynamic' ? (
          <p>
            <strong>🔄 Dynamic Mode:</strong> Filter options update based on your current selections. 
            Great for exploring what combinations are available in your data.
          </p>
        ) : (
          <p>
            <strong>📌 Static Mode:</strong> All filter options remain available regardless of selections. 
            Perfect for selecting multiple items from the same category (e.g., multiple KPI categories).
          </p>
        )}
      </div>

      {/* Always Visible Filter Content */}
      <div className="filter-content">
        <div className="filter-grid">
          {/* Brands Filter */}
          <div className="filter-group">
            <label className="filter-label">
              <span className="label-icon">🏢</span>
              Brands
            </label>
            <div className="filter-input-group">
              <select
                value={brandInput}
                onChange={(e) => handleBrandSelect(e.target.value)}
                className="modern-select"
                disabled={isLoadingOptions}
              >
                <option value="">
                  {isLoadingOptions ? 'Loading brands...' : 'Select a brand...'}
                </option>
                {availableOptions.brands.map(brand => (
                  <option key={brand} value={brand}>
                    {brand}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleAddBrand}
                disabled={!brandInput.trim() || filter.brands.includes(brandInput.trim())}
                className="add-btn"
                title="Add another brand to filter"
              >
                +
              </button>
              <button
                type="button"
                onClick={handleRefreshOptions}
                disabled={isLoadingOptions}
                className="refresh-btn"
                title="Refresh available options"
              >
                🔄
              </button>
            </div>
            {filter.brands.length > 0 && (
              <div className="filter-tags">
                {filter.brands.map(brand => (
                  <span key={brand} className="filter-tag">
                    {brand}
                    <button onClick={() => handleRemoveBrand(brand)}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Dealers Filter */}
          <div className="filter-group">
            <label className="filter-label">
              <span className="label-icon">🏪</span>
              Dealers
            </label>
            <div className="filter-input-group">
              <select
                value={dealerInput}
                onChange={(e) => handleDealerSelect(e.target.value)}
                className="modern-select"
                disabled={isLoadingOptions}
              >
                <option value="">
                  {isLoadingOptions ? 'Loading dealers...' : 'Select a dealer...'}
                </option>
                {availableOptions.dealers.map(dealer => (
                  <option key={dealer} value={dealer}>
                    {dealer}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleAddDealer}
                disabled={!dealerInput.trim() || filter.dealers.includes(dealerInput.trim())}
                className="add-btn"
                title="Add another dealer to filter"
              >
                +
              </button>
            </div>
            {filter.dealers.length > 0 && (
              <div className="filter-tags">
                {filter.dealers.map(dealer => (
                  <span key={dealer} className="filter-tag">
                    {dealer}
                    <button onClick={() => handleRemoveDealer(dealer)}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Years Filter */}
          <div className="filter-group">
            <label className="filter-label">
              <span className="label-icon">📅</span>
              Years
            </label>
            <div className="filter-input-group">
              <select
                value={yearInput}
                onChange={(e) => handleYearSelect(e.target.value)}
                className="modern-select"
                disabled={isLoadingOptions}
              >
                <option value="">
                  {isLoadingOptions ? 'Loading years...' : 'Select year...'}
                </option>
                {availableOptions.years.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleAddYear}
                disabled={!yearInput || filter.years.includes(parseInt(yearInput))}
                className="add-btn"
                title="Add another year to filter"
              >
                +
              </button>
            </div>
            {filter.years.length > 0 && (
              <div className="filter-tags">
                {filter.years.map(year => (
                  <span key={year} className="filter-tag">
                    {year}
                    <button onClick={() => handleRemoveYear(year)}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Months Filter */}
          <div className="filter-group">
            <label className="filter-label">
              <span className="label-icon">🗓️</span>
              Months
            </label>
            <div className="filter-input-group">
              <select
                value={monthInput}
                onChange={(e) => handleMonthSelect(e.target.value)}
                className="modern-select"
              >
                <option value="">Select month...</option>
                {months.filter(month => availableOptions.months.includes(month.value)).map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleAddMonth}
                disabled={!monthInput || filter.months.includes(parseInt(monthInput))}
                className="add-btn"
                title="Add another month to filter"
              >
                +
              </button>
            </div>
            {filter.months.length > 0 && (
              <div className="filter-tags">
                {filter.months.map(month => (
                  <span key={month} className="filter-tag">
                    {months.find(m => m.value === month)?.label || month}
                    <button onClick={() => handleRemoveMonth(month)}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* KPI Categories Filter */}
          <div className="filter-group">
            <label className="filter-label">
              <span className="label-icon">📊</span>
              KPI Categories
            </label>
            <div className="filter-input-group">
              <select
                value={categoryInput}
                onChange={(e) => handleCategorySelect(e.target.value)}
                className="modern-select"
                disabled={isLoadingOptions}
              >
                <option value="">
                  {isLoadingOptions ? 'Loading categories...' : 'Select category...'}
                </option>
                {availableOptions.kpiCategories.map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleAddCategory}
                disabled={!categoryInput || filter.kpiCategories.includes(categoryInput)}
                className="add-btn"
                title="Add another category to filter"
              >
                +
              </button>
            </div>
            {filter.kpiCategories.length > 0 && (
              <div className="filter-tags">
                {filter.kpiCategories.map(category => (
                  <span key={category} className="filter-tag">
                    {category}
                    <button onClick={() => handleRemoveCategory(category)}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Owner Filter - Only shown for admins and group admins */}
          {showOwnerFilter && (
            <div className="filter-group">
              <label className="filter-label">
                <span className="label-icon">👤</span>
                Owner
              </label>
              <div className="filter-input-group">
                <select
                  value={ownerInput}
                  onChange={(e) => handleOwnerSelect(e.target.value)}
                  className="modern-select"
                >
                  <option value="">Select owner...</option>
                  {filterUserIds && userMap ? (
                    // Use the userMap and filterUserIds if available
                    filterUserIds.map(userId => {
                      const user = userMap.get(userId);
                      return user ? (
                        <option key={userId} value={userId}>
                          {user.name}
                        </option>
                      ) : null;
                    })
                  ) : (
                    // Fall back to availableUsers if userMap is not available
                    availableUsers.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))
                  )}
                </select>
                <button
                  type="button"
                  onClick={handleAddOwner}
                  disabled={!ownerInput || filter.owners.includes(ownerInput)}
                  className="add-btn"
                  title="Add another owner to filter"
                >
                  +
                </button>
              </div>
              {filter.owners.length > 0 && (
                <div className="filter-tags">
                  {filter.owners.map(ownerId => {
                    // Try to find user in userMap first, then fall back to availableUsers
                    const user = userMap?.get(ownerId) || availableUsers.find(u => u.id === ownerId);
                    return (
                      <span key={ownerId} className="filter-tag">
                        {user?.name || ownerId}
                        <button onClick={() => handleRemoveOwner(ownerId)}>×</button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Filter Actions */}
        <div className="filter-actions">
          <button
            type="button"
            onClick={handleClearFilter}
            className="clear-filters-btn"
            disabled={activeFilterCount === 0}
          >
            <span>🗑️</span>
            Clear All
          </button>
          <button
            type="button"
            onClick={handleApplyFilter}
            className="apply-filters-btn"
            title="Manually refresh filters (filters auto-apply when changed)"
          >
            <span>🔄</span>
            Refresh
            {activeFilterCount > 0 && ` (${activeFilterCount})`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinancialRecordFilterPanel; 